import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { MessageCircle, Copy, Check, Lock, Crown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { fmtNaira, fmtDate, whatsappLink, publicUrl } from "@/lib/format";

const FREE_TONES = [
  { key: "friendly", label: "Friendly" },
  { key: "gentle", label: "Gentle" },
  { key: "firm", label: "Firm" },
  { key: "abeg", label: "Abeg" },
];

const PLUS_TONES = [
  { key: "formal", label: "Formal" },
  { key: "final", label: "Final notice" },
  { key: "deadline", label: "Set a deadline" },
  { key: "instalment", label: "Offer instalments" },
  { key: "warm", label: "Warm & appreciative" },
  { key: "novex", label: "No Vex" },
  { key: "sharp", label: "Sharp Sharp" },
];

function payByDate(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return fmtDate(d.toISOString());
}

function buildMessage(tone, debt) {
  const name = debt.person_name;
  const amt = fmtNaira(debt.outstanding_amount, debt.currency);
  const due = debt.due_date ? fmtDate(debt.due_date) : null;
  switch (tone) {
    case "friendly":
      return `Hi ${name}, just a quick reminder about the ${amt}. Please let me know when you'll be able to send it. Thanks.`;
    case "gentle":
      return `Hello ${name}, hope you're doing well. Just reminding you about the ${amt}${due ? ` due on ${due}` : ""}. I'd appreciate it if you could let me know when you'll be able to repay it.`;
    case "firm":
      return `Hi ${name}, your ${amt} repayment is now overdue. Please confirm when payment will be made. Thank you.`;
    case "formal":
      return `Dear ${name}, this is a reminder regarding the outstanding ${amt}${due ? ` which was due on ${due}` : ""}. Kindly advise on the expected date of repayment at your earliest convenience. Thank you.`;
    case "final":
      return `Hi ${name}, this is a final reminder about the ${amt} that remains outstanding${due ? ` since ${due}` : ""}. Please arrange repayment as soon as possible and confirm the date you'll settle it. Thank you.`;
    case "deadline":
      return `Hi ${name}, kindly help me settle the outstanding ${amt} by ${payByDate(3)} if you can. Please let me know if that timing works for you. Thanks.`;
    case "instalment":
      return `Hi ${name}, I understand things can be tight. If it's easier, we can split the ${amt} into smaller instalments. Let me know an amount and date you can start with. Thanks.`;
    case "warm":
      return `Hi ${name}, hope all is well! Whenever you get a moment, just a small reminder about the ${amt} — no rush at all, simply keeping us both on the same page. Thank you so much!`;
    case "abeg":
      return `Hi ${name} 👋 Abeg, just a small reminder about the ${amt}. Let me know when you fit sort am. Thank you 🙏`;
    case "novex":
      return `Hi ${name} 👋 No vex for the reminder o. Just checking on the ${amt}. Let me know when you fit sort am 🙏`;
    case "sharp":
      return `Hi ${name} 👋 Just a reminder about the ${amt}. If you fit sort am sharp sharp, I go appreciate am 🙏 Thank you!`;
    default:
      return "";
  }
}

export default function RemindSheet({ open, onOpenChange, debt }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPlus = user?.subscription_status === "plus";
  const [tone, setTone] = useState("friendly");
  const [copied, setCopied] = useState(false);
  const [link, setLink] = useState("");
  const base = useMemo(() => (debt ? buildMessage(tone, debt) : ""), [tone, debt]);
  const message = useMemo(
    () => (base ? (link ? `${base}\n\nView or repay this debt: ${link}` : base) : ""),
    [base, link]
  );

  // Fetch a secure debt link (never exposes bank details in the message itself).
  useEffect(() => {
    if (!open || !debt) return;
    setLink("");
    api.post(`/debts/${debt.id}/share-link`)
      .then((r) => setLink(publicUrl(r.data.path)))
      .catch(() => {});
  }, [open, debt]);

  if (!debt) return null;

  const logReminder = () => api.post(`/debts/${debt.id}/reminder`).catch(() => {});

  const pickTone = (t, locked) => {
    if (locked) {
      toast("Advanced tones are a Debii Plus feature");
      onOpenChange(false);
      navigate("/upgrade");
      return;
    }
    setTone(t.key);
  };

  const sendWhatsApp = () => {
    logReminder();
    window.open(whatsappLink(debt.person_phone, message), "_blank");
    onOpenChange(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      logReminder();
      toast.success("Message copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy — please select the text manually.");
    }
  };

  const ToneButton = ({ t, locked }) => (
    <button
      key={t.key}
      data-testid={`tone-${t.key}-option`}
      onClick={() => pickTone(t, locked)}
      className={`relative h-11 rounded-xl text-sm font-semibold border transition-all px-2 ${
        tone === t.key
          ? "bg-primary text-primary-foreground border-primary"
          : locked
          ? "bg-white text-muted-foreground border-border"
          : "bg-white text-foreground border-border"
      }`}
    >
      <span className="inline-flex items-center gap-1">
        {locked && <Lock className="h-3 w-3" />}
        {t.label}
      </span>
    </button>
  );

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-w-[30rem] mx-auto">
        <DrawerHeader className="text-left">
          <DrawerTitle className="font-display text-xl">Remind {debt.person_name}</DrawerTitle>
        </DrawerHeader>
        <div className="px-5 pb-8 space-y-4 max-h-[80vh] overflow-y-auto no-scrollbar">
          <div className="grid grid-cols-3 gap-2">
            {FREE_TONES.map((t) => <ToneButton key={t.key} t={t} locked={false} />)}
          </div>

          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Crown className="h-3.5 w-3.5 text-amber-500" />
              <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                Advanced tones{!isPlus && " · Plus"}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {PLUS_TONES.map((t) => <ToneButton key={t.key} t={t} locked={!isPlus} />)}
            </div>
          </div>

          <div data-testid="reminder-preview" className="rounded-2xl bg-secondary p-4 text-sm leading-relaxed text-foreground">
            {message}
          </div>
          <p className="text-xs text-muted-foreground text-center">
            Debii never messages anyone for you — you send it yourself.
          </p>
          <div className="space-y-2">
            <Button data-testid="whatsapp-send-button" onClick={sendWhatsApp} className="w-full h-12 rounded-xl text-base bg-[#25D366] hover:bg-[#20bd5a] text-white">
              <MessageCircle className="h-5 w-5" /> Send via WhatsApp
            </Button>
            <Button data-testid="reminder-copy-button" variant="outline" onClick={copy} className="w-full h-11 rounded-xl">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} Copy message
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
